import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Navbar from './components/Navbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import CaregiverDashboard from './pages/CaregiverDashboard';
import UserProfile from './pages/UserProfile';
import PetProfiles from './pages/PetProfiles';
import SearchCaregivers from './pages/SearchCaregivers';
import CaregiverProfile from './pages/CaregiverProfile';
import CaregiverPublicProfile from './pages/CaregiverPublicProfile';
import EditCaregiverProfile from './pages/EditCaregiverProfile';
import BookingFlow from './pages/BookingFlow';
import Messages from './pages/Messages';
import OwnerBookings from './pages/OwnerBookings';
import Reviews from './pages/Reviews';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import AdminBadges from './pages/AdminBadges';
import Reporting from './pages/Reporting';
import MyServices from './pages/MyServices';
import CaregiverBookingsSchedule from './pages/CaregiverBookingsSchedule';
import Availability from './pages/Availability';
import HowItWorks from './pages/HowItWorks';
import Testimonials from './pages/Testimonials';
import OfflineJobs from './pages/OfflineJobs';
import ExpenseTracker from './pages/ExpenseTracker';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import BlogList from './pages/blogs/BlogList';
import BlogPost from './pages/blogs/BlogPost';
import AdminMarketing from './pages/AdminMarketing';
import BlogEditor from './pages/BlogEditor';
import SettingsRedirect from './components/SettingsRedirect';
import SavedCaregivers from './pages/SavedCaregivers';
import NotFound from './pages/NotFound';
import IconLibrary from './pages/IconLibrary';
import AnimatedPage from './components/ui/AnimatedPage';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import ServiceStatusBanner from './components/ServiceStatusBanner';
import useNativeBackButton from './hooks/useNativeBackButton';

// Chatbot wrapper component to exclude from admin pages
// TODO: Add admin control to enable/disable chatbot
const ChatbotWrapper = () => {
  // Temporarily disabled - will be controlled from admin panel
  return null;
  
  /*
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (isAdminPage) {
    return null;
  }
  
  return <Chatbot />;
  */
};

function AppShell() {
  const location = useLocation();
  useNativeBackButton();
  const hideNavbar =
    location.pathname.startsWith('/admin-') ||
    location.pathname === '/icon-library';
  const hideFooter =
    hideNavbar ||
    location.pathname === '/login' ||
    location.pathname === '/signup';

  return (
    <div className="App pf-shell flex flex-col min-h-screen">
      <ServiceStatusBanner />
      <PwaInstallPrompt />
      {!hideNavbar && <Navbar />}
      <div className="flex-1">
      <AnimatedPage>
      <Routes>
              {/* Public pages */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/become-caregiver" element={<Signup />} />
              <Route path="/icon-library" element={<IconLibrary />} />
              <Route path="/search" element={<SearchCaregivers />} />
              <Route path="/caregiver/:caregiverId" element={<CaregiverPublicProfile />} />
              <Route path="/caregiver-profile/:id" element={<CaregiverProfile />} />
              
              {/* PROFILE ROUTES - Only for caretaker and petowner, NOT admin */}
              <Route path="/profile/:userId" element={
                <ProtectedRoute excludeRoles={['admin']}>
                  <UserProfile />
                </ProtectedRoute>
              } />
              <Route path="/owner-profile" element={
                <ProtectedRoute excludeRoles={['admin']}>
                  <UserProfile />
                </ProtectedRoute>
              } />
              <Route path="/caregiver-profile" element={
                <ProtectedRoute excludeRoles={['admin']}>
                  <UserProfile />
                </ProtectedRoute>
              } />
              
              {/* OWNER ROUTES - all prefixed with /owner- */}
              <Route path="/owner-dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/owner-pets" element={
                <ProtectedRoute>
                  <PetProfiles />
                </ProtectedRoute>
              } />
              
              <Route path="/owner-booking/:caregiverId" element={
                <ProtectedRoute>
                  <BookingFlow />
                </ProtectedRoute>
              } />
              
              <Route path="/saved-caregivers" element={
                <ProtectedRoute excludeRoles={['admin']}>
                  <SavedCaregivers />
                </ProtectedRoute>
              } />

              <Route path="/owner-bookings" element={
                <ProtectedRoute>
                  <OwnerBookings />
                </ProtectedRoute>
              } />
              
              <Route path="/owner-messages" element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              } />
              
              <Route path="/owner-reviews" element={
                <ProtectedRoute>
                  <Reviews />
                </ProtectedRoute>
              } />
              
              <Route path="/owner-settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />

              <Route path="/owner-offline-jobs" element={
                <ProtectedRoute>
                  <OfflineJobs />
                </ProtectedRoute>
              } />

              {/* CAREGIVER ROUTES - all prefixed with /caregiver- */}
              <Route path="/caregiver-dashboard" element={
                <ProtectedRoute>
                  <CaregiverDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/edit-caregiver-profile" element={
                <ProtectedRoute>
                  <EditCaregiverProfile />
                </ProtectedRoute>
              } />
              
              <Route path="/caregiver-services" element={
                <ProtectedRoute>
                  <MyServices />
                </ProtectedRoute>
              } />
              
              <Route path="/caregiver-bookings" element={
                <ProtectedRoute>
                  <CaregiverBookingsSchedule />
                </ProtectedRoute>
              } />
              
              <Route path="/caregiver-availability" element={
                <ProtectedRoute>
                  <Availability />
                </ProtectedRoute>
              } />

              <Route path="/caregiver-messages" element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              } />

              <Route path="/caregiver-reviews" element={
                <ProtectedRoute>
                  <Reviews />
                </ProtectedRoute>
              } />

              <Route path="/caregiver-settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />

              <Route path="/caregiver-offline-jobs" element={
                <ProtectedRoute>
                  <OfflineJobs />
                </ProtectedRoute>
              } />

              <Route path="/caregiver-expenses" element={
                <ProtectedRoute>
                  <ExpenseTracker />
                </ProtectedRoute>
              } />

              {/* ADMIN ROUTES - all prefixed with /admin- */}
              <Route path="/admin-dashboard" element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/admin-reporting" element={
                <ProtectedRoute adminOnly>
                  <Reporting />
                </ProtectedRoute>
              } />
              
              <Route path="/admin-badges" element={
                <ProtectedRoute adminOnly>
                  <AdminBadges />
                </ProtectedRoute>
              } />

              <Route path="/admin-settings" element={
                <ProtectedRoute adminOnly>
                  <Settings />
                </ProtectedRoute>
              } />
              
              <Route path="/admin-marketing" element={
                <ProtectedRoute adminOnly>
                  <AdminMarketing />
                </ProtectedRoute>
              } />
              
              <Route path="/admin-marketing/blog/new" element={
                <ProtectedRoute adminOnly>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              
              <Route path="/admin-marketing/blog/edit/:id" element={
                <ProtectedRoute adminOnly>
                  <BlogEditor />
                </ProtectedRoute>
              } />

              {/* Legacy routes - redirect to new prefixed routes */}
              <Route path="/dashboard" element={<Home />} />
              <Route path="/pets" element={<Home />} />
              <Route path="/my-bookings" element={<Home />} />
              <Route path="/messages" element={<Home />} />
              <Route path="/reviews" element={<Home />} />
              <Route path="/settings" element={<SettingsRedirect />} />
              <Route path="/my-services" element={<Home />} />
              <Route path="/schedule" element={<Home />} />
              <Route path="/availability" element={<Home />} />
              <Route path="/offline-jobs" element={<Home />} />
              <Route path="/expenses" element={<Home />} />
              
              {/* Catch all - show a real 404 so broken links surface instead of silently rendering Home */}
              <Route path="*" element={<NotFound />} />
            </Routes>
      </AnimatedPage>
      </div>
      {!hideFooter && <Footer />}
            <ChatbotWrapper />
          </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DataProvider>
          <Router>
            <AppShell />
          </Router>
        </DataProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
